import { LoadingOverlay, Modal } from '@mantine/core';
import { QueryObserverResult } from '@tanstack/react-query';
import { getOr } from 'lodash/fp';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ServerError } from '@portals/api';
import {
  DeviceType,
  GettingStartedToursNamesEnum,
  useAddDevice,
  useCompleteGettingStartedTour,
  useDeviceModels,
  useIsFirstClaimedDevice,
  useOrganizationConfig,
  usePortalCapabilities,
} from '@portals/api/organizations';
import {
  isCustomerFeatureAll,
  isCustomerFeatureLabOnly,
} from '@portals/framework';
import { ReactComponent as TrendUp } from '@portals/icons/bulk/trend-up.svg';
import { useOpenModal } from '@portals/redux';
import { toastrError, toastrSuccess } from '@portals/redux/actions/toastr';
import { NO_DEVICE_MONITORING_LICENSES } from '@portals/utils';

import { AddDeviceForm } from './AddDeviceForm';
import NoLicense from './NoLicense';
import { TourCompletedModalProps } from '../TourCompletedModal';

export const AddDevice = ({ data, closeMe }) => {
  const dispatch = useDispatch();
  const deviceModels = useDeviceModels();
  const addDevice = useAddDevice();
  const isNoLicense = useSelector(
    getOr(false, ['data', 'system_issues', NO_DEVICE_MONITORING_LICENSES])
  );
  const navigate = useNavigate();
  const openModal = useOpenModal();
  const completeGettingStartedTour = useCompleteGettingStartedTour();
  const portalCapabilities = usePortalCapabilities();
  const isFirstClaimedDevice = useIsFirstClaimedDevice();
  const organizationConfig = useOrganizationConfig();

  const spaceId = data;

  const showClaimDeviceTourCompletedModal = useCallback(
    (response: QueryObserverResult<boolean, ServerError>) => {
      return (
        response.data &&
        (isCustomerFeatureAll(portalCapabilities.setup_organization) ||
          isCustomerFeatureLabOnly(
            portalCapabilities.setup_organization,
            organizationConfig.lab
          ))
      );
    },
    [organizationConfig.lab, portalCapabilities.setup_organization]
  );

  const handleAdd = async ({
    name,
    model,
    mac,
    sn,
    cloud_id,
    zoom_room,
    c2c_id,
    license_id,
  }) => {
    try {
      const response = await addDevice.mutateAsync({
        space_id: spaceId,
        name: name.trim(),
        device_model_id: model,
        mac,
        sn,
        cloud_id,
        zoom_room,
        c2c_id,
        license_id,
      });

      handleSuccess(response);
    } catch (err) {
      handleError(err?.error);
      console.error(err);
    }
  };

  const handleSuccess = async (device: DeviceType) => {
    dispatch(
      toastrSuccess(
        'Successfully added device',
        `${device.name || device.partner.model}`
      )
    );

    const response = await isFirstClaimedDevice.refetch();

    closeMe();

    if (showClaimDeviceTourCompletedModal(response)) {
      openModal<TourCompletedModalProps['data']>('TourCompletedModal', {
        title: 'You’ve just claimed your first device',
        nextTourTitle: 'Monitor and manage devices',
        nextTourDescription:
          'Learn how to effectively monitor, manage, and configure your devices, empowering you to keep track of their performance and make necessary adjustments as needed.',
        nextTourIcon: <TrendUp />,
        onNextTourClick: () => {
          navigate(
            `/overview/?active_tour=${GettingStartedToursNamesEnum.Monitoring}`
          );
        },
      });

      completeGettingStartedTour.mutate('claim_device_completed');
    }
  };

  const handleError = (error?: string) => {
    if (error) {
      dispatch(toastrError(error));
    }
  };

  if (!deviceModels.isFetched)
    return (
      <Modal opened onClose={closeMe}>
        <LoadingOverlay visible />
      </Modal>
    );

  return (
    <Modal opened onClose={closeMe} title="Add device to space">
      {addDevice.isLoading ? <LoadingOverlay visible /> : null}

      {isNoLicense ? (
        <NoLicense closeMe={closeMe} />
      ) : (
        <AddDeviceForm handleClose={closeMe} handleAdd={handleAdd} />
      )}
    </Modal>
  );
};
